import React, { useCallback, useEffect, useState } from "react";
import {
  X, Plus, Save, Check, Search, CircleCheck, CircleMinus,
  Circle, Loader2, CheckCircle2, XCircle, Archive,
  Calendar, Edit2,
} from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Badge from "../common/Badge";
import Skeleton from "../common/Skeleton";
import NoDataFound from "../common/NoDataFound";
import { confirmDelete, confirmWarning, confirmActive } from "../common/confirm";
import {
  tasksService, Task, TaskListParams,
} from "../../services/tasksService";
import { entityRegistryService, EntityType } from "../../services/entityRegistryService";
import { userService, User } from "../../services/userService";
import { filterPickableUsers } from "../../utils/userPickable";
import TaskForm from "../tasks/TaskForm";
import FormControl from "../common/FormControl";
import Toast, { ToastMessage } from "../common/Toast";
import { statusVariant } from "../../utils/statusVariant";
import { usePermissions, usePermission, NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";

const ACT_TASKS = "GOV_TASKS";

const SOURCE_FILTER_TYPES = ["MEETING", "AGENDA_ITEM", "CAPA_ISSUE", "PROJECT"];

interface BucketDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  accent: string;
  emptyText: string;
  filter: (t: Task) => boolean;
  // Status that the column itself represents. A card whose status matches this
  // is redundant, so its badge is hidden; cards with a different status (e.g.
  // VERIFIED inside the DONE column) keep their badge.
  primaryStatus: string;
}

const BUCKETS: BucketDef[] = [
  {
    key: "open",
    label: "Open",
    icon: <Circle size={13} />,
    accent: "#f59e0b",
    emptyText: "No open tasks.",
    filter: (t) => t.status === "OPEN",
    primaryStatus: "OPEN",
  },
  {
    key: "in_progress",
    label: "In Progress",
    icon: <Loader2 size={13} />,
    accent: "#0284c7",
    emptyText: "Nothing in progress.",
    filter: (t) => t.status === "IN_PROGRESS" || t.status === "REVERTED",
    primaryStatus: "IN_PROGRESS",
  },
  {
    key: "done",
    label: "Done",
    icon: <CheckCircle2 size={13} />,
    accent: "#10b981",
    emptyText: "No completed tasks yet.",
    filter: (t) => t.status === "DONE" || t.status === "VERIFIED",
    primaryStatus: "DONE",
  },
  {
    key: "closed",
    label: "Closed / Cancelled",
    icon: <XCircle size={13} />,
    accent: "#6b7280",
    emptyText: "Nothing here.",
    filter: (t) => t.status === "REJECTED" || t.status === "CANCELLED" || t.status === "ARCHIVED",
    primaryStatus: "",
  },
];

const BucketSkeleton: React.FC = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "8px 4px" }}>
    {[1, 2, 3].map((i) => (
      <div key={i} className="action-card action-card--skeleton">
        <div className="action-card__skel-header">
          <Skeleton.Box width={34} height={34} radius={8} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Skeleton.Box width="70%" height={12} />
            <Skeleton.Box width="45%" height={10} />
          </div>
          <Skeleton.Pill width={56} height={20} />
        </div>
        <div className="action-card__skel-meta">
          <Skeleton.Box width={80} height={10} />
          <Skeleton.Box width={100} height={10} />
        </div>
      </div>
    ))}
  </div>
);

const GovernanceTasks: React.FC = () => {
  const { formatDate } = useEntitySettings();
  const { check } = usePermissions();
  const { allowed: canView, loading: viewLoading } = usePermission(ACT_TASKS, 'view');
  const canModify = check(ACT_TASKS, 'view+modify').allowed;
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const [search, setSearch] = useState("");
  const [sourceTypeCode, setSourceTypeCode] = useState<string>("");
  const [doerId, setDoerId] = useState<number | "">("");
  const [dueBefore, setDueBefore] = useState<string>("");

  const [users, setUsers] = useState<User[]>([]);
  const [sourceTypes, setSourceTypes] = useState<EntityType[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fmtDate = (d?: string | null) => {
    if (!d) return "";
    return formatDate(d);
  };

  useEffect(() => {
    userService.getAllUsers({ limit: 200 }).then((r) => setUsers(filterPickableUsers(r.data || []))).catch(() => {});
    entityRegistryService.listTypes(true)
      .then((types) => setSourceTypes(types.filter((t) => SOURCE_FILTER_TYPES.includes(t.type_code))))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params: TaskListParams = {
        q: search || undefined,
        source_type_code: sourceTypeCode || undefined,
        doer_id: doerId === "" ? undefined : Number(doerId),
        due_before: dueBefore || undefined,
        limit: 500,
      };
      const data = await tasksService.list(params);
      setTasks(data);
    } catch (e: any) {
      setToast({ type: "error", message: e?.response?.data?.detail || e?.message || "Failed to load tasks" });
    } finally {
      setLoading(false);
    }
  }, [search, sourceTypeCode, doerId, dueBefore, canView]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (task: Task) => {
    // For edits, stay in viewing mode; for creates, close the modal
    if (editingTask && editingTask.id) {
      setEditingTask(task);
      setIsEditing(false);
      load();
    } else {
      setShowForm(false);
      setEditingTask(null);
      setIsEditing(false);
      load();
    }
  };

  const handleEdit = (t: Task) => { setEditingTask(t); setShowForm(true); setIsEditing(false); };

  const handleEnterEditMode = () => { setIsEditing(true); };

  const handleDelete = async (t: Task) => {
    const ok = await confirmDelete({ text: `This will permanently delete the task "${t.name}".` });
    if (!ok) return;
    try {
      await tasksService.remove(t.id);
      setToast({ type: "delete", message: "Task deleted successfully" });
      load();
    } catch (e: any) {
      setToast({ type: "error", message: e?.response?.data?.detail || e?.message || "Failed to delete" });
    }
  };

  const handleToggleActive = async () => {
    if (!editingTask) return;
    const next = editingTask.is_active ? 0 : 1;
    const isActive = editingTask.is_active;
    const confirmFn = isActive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? "Reactivate this task?" : "Inactivate this task?",
      text: next ? "It will appear in active lists again." : "It will be hidden from active lists.",
      confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    try {
      await tasksService.update(editingTask.id, { is_active: next } as any);
      setToast({ type: next ? "success" : "warning", message: next ? "Reactivated" : "Inactivated" });
      setShowForm(false);
      setEditingTask(null);
      load();
    } catch (e: any) {
      setToast({ type: "error", message: e?.response?.data?.detail || e?.message || "Failed to update status" });
    } finally {
      setToggling(false);
    }
  };

  useEffect(() => { setSubmitting(false); setToggling(false); setIsEditing(false); }, [showForm]);

  const hasActiveFilters = search || sourceTypeCode || doerId !== "" || dueBefore;

  const today = new Date(); today.setHours(0, 0, 0, 0);

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to tasks." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            value={sourceTypeCode || null}
            onChange={(v) => setSourceTypeCode((v as string) ?? "")}
            options={sourceTypes.map((t) => ({ value: t.type_code, label: t.display_name }))}
            placeholder="All Sources"
            clearable
            className="filter-status--wide"
            data-testid="SEL_task_source_filter"
          />
          <FormControl.SearchSelect
            value={doerId === "" ? null : doerId}
            onChange={(v) => setDoerId(v === null ? "" : Number(v))}
            options={users.map((u) => ({
              value: u.id,
              label: `${u.first_name || ""} ${u.last_name || ""}${u.login_id ? ` (${u.login_id})` : ""}`.trim(),
            }))}
            placeholder="All Doers"
            clearable
            className="filter-status--wide"
            data-testid="SEL_task_doer_filter"
          />
          <FormControl.DatePicker
            value={dueBefore}
            onChange={(e: { target: { value: string } }) => setDueBefore(e.target.value)}
            placeholder="Due before"
            className="filter-status"
            data-testid="INP_task_due_before"
          />
          <FormControl.Input
            type="text"
            placeholder="Search tasks…"
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_task_search"
          />
          {hasActiveFilters && (
            <Button
              variant="outline"
              icon={X}
              onClick={() => { setSearch(""); setSourceTypeCode(""); setDoerId(""); setDueBefore(""); }}
              data-testid="BTN_task_clear_filters"
            >
              Clear
            </Button>
          )}
          <Button
            variant="fill"
            icon={Plus}
            onClick={() => { setEditingTask(null); setShowForm(true); }}
            disabled={viewLoading || !canModify}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            data-testid="BTN_task_create"
          >
            Create
          </Button>
        </div>
      </div>

      {/* Kanban board */}
      <div className="netbody-content">
        <div className="scroll-container">
          {!loading && tasks.length === 0 ? (
            <NoDataFound title="No Tasks Found" message="No tasks found. Click Create to add one." />
          ) : (
            <div className="bucket-grid bucket-grid--4" style={{ height: "100%", padding: "8px" }}>
              {BUCKETS.map((bucket) => {
                // Newest-first within each column: a task you just created
                // surfaces at the top instead of being buried by the backend's
                // due-date sort (which pushes no-due-date tasks to the bottom).
                const items = loading
                  ? []
                  : tasks.filter(bucket.filter).sort((a, b) => b.id - a.id);
                return (
                  <div
                    key={bucket.key}
                    className="bucket-panel"
                    style={{ "--accent": bucket.accent } as React.CSSProperties}
                  >
                    <div className="bucket-panel-header">
                      <span className="bucket-panel-icon">{bucket.icon}</span>
                      <span className="bucket-panel-title">{bucket.label.toUpperCase()}</span>
                      <span className="bucket-panel-count">{loading ? "…" : items.length}</span>
                    </div>

                    <div className="bucket-panel-scroll">
                      {loading ? (
                        <BucketSkeleton />
                      ) : items.length === 0 ? (
                        <div className="bucket-empty-state">
                          <Archive size={28} className="bucket-empty-icon" />
                          <span className="bucket-empty-text">{bucket.emptyText}</span>
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "8px 4px" }}>
                          {items.map((t) => {
                            // Build one flat list: source first, then linked entities
                            const links = t.linked_entities || [];
                            const fallbackLink = !links.length && t.linked_type_code && t.linked_label
                              ? [{ id: -1, type_code: t.linked_type_code, label: t.linked_label }]
                              : [];
                            const sourceItem = t.source_type_code
                              ? [{ id: -2, type_code: t.source_type_code, label: t.source_label ?? "" }]
                              : [];
                            const allMeta = [...sourceItem, ...links, ...fallbackLink];
                            const isOverdue = t.due_date && new Date(t.due_date) < today
                              && !["DONE", "VERIFIED", "REJECTED", "CANCELLED"].includes(t.status);

                            return (
                              <div
                                key={t.id}
                                data-testid={`BTN_gov_tasks_row_${t.id}_open`}
                                className="action-card"
                                role="button"
                                tabIndex={0}
                                onClick={() => handleEdit(t)}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleEdit(t); } }}
                              >
                                <div className="action-card__header">
                                  <div className="action-card__title-block">
                                    {t.my_role && (
                                      <span className="action-card__subtitle" style={{ textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: "var(--font-weight-heading)" }}>
                                        {t.my_role.replace(/_/g, " ")}
                                      </span>
                                    )}
                                    <span className="action-card__title">{t.name}</span>
                                    {t.description && (
                                      <span className="action-card__subtitle action-card__subtitle--desc">{t.description}</span>
                                    )}
                                  </div>
                                  {t.status !== bucket.primaryStatus && (
                                    <div className="action-card__badge">
                                      <Badge variant={statusVariant(t.status)} size="sm">
                                        {t.status.replace(/_/g, " ")}
                                      </Badge>
                                    </div>
                                  )}
                                </div>

                                {(t.due_date || allMeta.length > 0) && (
                                  <div className="action-card__meta">
                                    {t.due_date && (
                                      <span className={`action-card__meta-item${isOverdue ? " action-card__meta-item--overdue" : ""}`}>
                                        <Calendar size={11} />
                                        {fmtDate(t.due_date)}
                                      </span>
                                    )}
                                    {allMeta.length > 0 && (
                                      <span className="action-card__meta-item">
                                        {allMeta[0].type_code && <span className="status-badge sm neutral">{allMeta[0].type_code}</span>}
                                        <span style={{ maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{allMeta[0].label}</span>
                                        {allMeta.length > 1 && <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>+{allMeta.length - 1}</span>}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditingTask(null); setIsEditing(false); setSubmitting(false); setToggling(false); }} size="lg">
        <Modal.Header>
          {editingTask ? (
            <>{isEditing ? "Editing" : "Viewing"} "<span className="item-highlight">{editingTask.name}</span>"</>
          ) : "Create Task"}
        </Modal.Header>
        <Modal.Body>
          <TaskForm
            formId="task-form"
            initial={editingTask || undefined}
            onSaved={handleSaved}
            onCancel={() => { setShowForm(false); setEditingTask(null); setIsEditing(false); }}
            onSubmittingChange={setSubmitting}
            disabled={editingTask ? !isEditing : false}
          />
        </Modal.Body>
        {editingTask ? (
          <Modal.Footer className="app-modal-footer--split">
            <Button
              variant={editingTask.is_active ? "inactive-outline" : "active-outline"}
              icon={editingTask.is_active ? CircleMinus : CircleCheck}
              onClick={handleToggleActive}
              disabled={toggling || submitting || isEditing}
              data-testid="BTN_task_toggle_active"
            >
              {toggling ? "..." : editingTask.is_active ? "Inactive" : "Active"}
            </Button>
            <div style={{ display: "flex", gap: 8 }}>
              {canModify && editingTask && editingTask.id && !isEditing && (
                <Button
                  variant="fill"
                  icon={Edit2}
                  onClick={handleEnterEditMode}
                  disabled={toggling || submitting}
                  data-testid="BTN_task_edit"
                >
                  Edit
                </Button>
              )}
              {canModify && editingTask && editingTask.id && isEditing && (
                <Button
                  type="submit"
                  form="task-form"
                  variant="update"
                  icon={Check}
                  disabled={submitting || toggling}
                  data-testid="BTN_task_update"
                >
                  {submitting ? "Updating..." : "Update"}
                </Button>
              )}
            </div>
          </Modal.Footer>
        ) : (
          <Modal.Footer>
            <Button type="submit" form="task-form" variant="fill" icon={Save} disabled={submitting} data-testid="BTN_task_submit">
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default GovernanceTasks;
